import React, { useState } from "react";
import { useContinentRegionContext } from "../../../../../context/continentRegion/context/ContinentRegionContext";
import { getCurrentRegion } from "../../../../../middlewares/sysNavigation/SysNavigation";
import {
  Title,
  GoBack,
  TextArea,
  InputField,
  MyMessage,
  MyActionButton,
  MyButton,
} from "../../../component/Component";
import { FOLDERS } from "../../../../doseeData/utils/Utils";
import { ImageKitUpload } from "../../../../../middlewares/libraries/imageKit/ImageKitLibrary";
import { defaultImage } from "../../../../../middlewares/libraries/imageKit/ImageKit";
import GenerateDescription from "../../../../doseeData/utils/GenerateDescription";

const editTilte = "Edit Region";
const regionRoute = "/region/manage";

const EditRegion = () => {
  const { updateRegion } = useContinentRegionContext();
  const region = getCurrentRegion()[0];

  const region_id = region?.continent_region_id;
  const [name, setName] = useState(region?.name);
  const [thumbnail, setThumbnail] = useState(region?.thumbnail);
  const [thumbnailId, setThumbnailId] = useState(region?.image_cdn_id);
  const [description, setDescription] = useState(region?.description);
  const [numOfCountry, setNumOfCountry] = useState(region?.country_number);
  const [showDescriptionModal, setShowDescriptionModal] = useState(null);

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const responses = (error, message) => {
    setError(error);
    setLoading(false);
    setSuccess(!error);
    setErrorMessage(message);
  };

  const sendEditRegionRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) responses(true, "Image Required");
    else await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = {
      name: name,
      slug: name.toLowerCase().replace(/\s/g, "_"),
      continent_id: 1,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "",
      description: description,
      country_number: numOfCountry,
    };

    const { status, message } = await updateRegion(region_id, data);
    if (status === 200) responses(false, message);
    else responses(true, message);
  };

  const slugPreview =
    name && name.trim() !== "" ? name.toLowerCase().replace(/\s/g, "_") : "";

  return (
    <div className="p-4 lg:p-6 h-full overflow-y-auto bg-gray-50">
      <div className="mx-auto max-w-5xl">
        <GoBack props={{ route: regionRoute }} />

        {showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null}

        {/* Global toast message */}
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />

        {/* Header */}
        <div className="mb-4">
          <Title props={{ title: editTilte }} />
        </div>

        {/* Card */}
        <form
          onSubmit={sendEditRegionRequest}
          className="rounded-2xl border border-gray-200 bg-white shadow-sm"
        >
          {/* Card header */}
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                Region Information
              </h2>
              <p className="mt-1 text-[11px] text-gray-500">
                Update basic details and media. Make sure the thumbnail looks sharp.
              </p>
            </div>
            {slugPreview ? (
              <span className="hidden md:inline text-[11px] text-gray-500">
                Slug: <span className="font-mono">{slugPreview}</span>
              </span>
            ) : null}
          </div>

          {/* Card body */}
          <div className="px-5 pb-5 pt-4 space-y-8">
            {/* Thumbnail */}
            <div className="w-full">
              <ImageKitUpload
                thumbnail={thumbnail}
                thumbnailId={thumbnailId}
                setThumbnail={setThumbnail}
                setThumbnailId={setThumbnailId}
                folder={FOLDERS.CONTINENT_REGIONS}
              />
            </div>

            {/* Fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <InputField
                  props={{
                    id: "name",
                    title: "Name",
                    type: "text",
                    value: name ? name : "",
                    onChange: (e) => {
                      setName(e.target.value);
                      setError(null);
                      setSuccess(null);
                    },
                  }}
                />
                {slugPreview ? (
                  <p className="mt-1 text-[11px] text-gray-500">
                    Slug preview: <span className="font-mono">{slugPreview}</span>
                  </p>
                ) : null}
              </div>

              <InputField
                props={{
                  id: "number_of_countries",
                  title: "Number of Countries",
                  type: "number",
                  value: numOfCountry ? numOfCountry : "",
                  onChange: (e) => {
                    setNumOfCountry(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
            </div>

            {/* Description */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-900">Description</p>
                <MyButton
                  onClick={() => setShowDescriptionModal(true)}
                  className="bg-gray-900 hover:bg-gray-800 text-white text-xs font-semibold rounded-lg px-3 py-2"
                >
                  Generate Description
                </MyButton>
              </div>

              <TextArea
                props={{
                  id: "description",
                  title: "",
                  type: "text",
                  value: description ? description : "",
                  onChange: (e) => {
                    setDescription(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />

              <p className="text-[11px] text-gray-500">
                Tip: Keep it concise and informative. Avoid duplicate content.
              </p>
            </div>
          </div>

          {/* Card footer / actions */}
          <div className="flex items-center justify-center gap-3 border-t px-5 py-4">
            <MyActionButton
              loading={loading}
              title="Save Changes"
              onClick={() => {
                setError(false);
                setSuccess(false);
                setErrorMessage(null);
              }}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRegion;
